import {
  Pressable,
  StyleSheet,
  Text,
  View,
  ViewStyle,
  TextStyle,
} from 'react-native';
import React, {useEffect, useState} from 'react';
import {UserStoryStatus} from 'constants/enums';
import {UserStoryTag, Icon} from 'ui-kits';
import {IconType} from 'constants/icons';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';

type UserStoryTags = keyof typeof UserStoryStatus;
const tagsArray: UserStoryTags[] = Object.keys(
  UserStoryStatus,
) as any as UserStoryTags[];

type sizeTypes = 'lg' | 'md' | 'sm';

type propType = Readonly<{
  onSave?: (valie: UserStoryTags) => void;
  value?: UserStoryTags;
  label?: string;
  placeholder?: string;
  helperLabel?: string;
  optionalLabel?: string;
  error?: string;
  size?: sizeTypes;
  rightIcon?: IconType;
  required?: boolean;
}>;
export function UserStoryStatusDropDown(props: propType) {
  const {
    onSave,
    value,
    label,
    placeholder = 'Select status',
    helperLabel,
    optionalLabel,
    error,
    size = 'md',
    required,
    rightIcon = 'cheveron_down',
  } = props;

  const [dropDownOpen, setDropDownOpen] = useState<boolean>(false);
  const [selectedValue, setSelectedValue] = useState<UserStoryTags>(value ?? 1);

  function handleSelect(selectionOption: UserStoryTags) {
    setSelectedValue(selectionOption);
    onSave?.(selectionOption);
    setDropDownOpen(false);
  }

  useEffect(() => {
    if (value) {
      setSelectedValue(value);
    }
  }, [value]);

  return (
    <View style={[styles.container, sizeStyles[size].container]}>
      {/* Label Section */}
      <View style={styles.labelWrapper}>
        <Text style={styles.label}>
          {label} {required ? <Text style={styles.redAsterisk}>*</Text> : null}
        </Text>
        <Text style={styles.optionalLabel}>{optionalLabel}</Text>
      </View>

      {/* Dropdown Button */}
      <Pressable
        style={[styles.inputWrapper, sizeStyles[size].inputWrapper]}
        onPress={() => setDropDownOpen(!dropDownOpen)}>
        <Text style={styles.placeholder}>
          {UserStoryStatus[selectedValue] || placeholder}
        </Text>
        <Icon type={rightIcon} size={14} />
      </Pressable>

      {/* Helper Text */}
      {helperLabel ? (
        <Text style={styles.helperLabel}>{helperLabel}</Text>
      ) : null}

      {/* Error Message */}
      {error ? <Text style={styles.errorLabel}>{error}</Text> : null}

      {/* Dropdown List */}
      {dropDownOpen ? (
        <View style={styles.dropdownWrapper}>
          <View style={styles.dropdownContainer}>
            {tagsArray.map(item => (
              <Pressable key={item} onPress={() => handleSelect(item)}>
                <UserStoryTag
                  style={styles.tag}
                  variant={UserStoryStatus[item]}
                />
              </Pressable>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

type styleProps = {
  label: TextStyle;
  placeholder: TextStyle;
  container: ViewStyle;
  inputWrapper: ViewStyle;
  helperLabel: TextStyle;
  optionalLabel: TextStyle;
  errorLabel: TextStyle;
  dropdownContainer: ViewStyle;
  dropdownWrapper: ViewStyle;
  redAsterisk: TextStyle;
  labelWrapper: ViewStyle;
  tag: ViewStyle;
};
const styles: styleProps = StyleSheet.create({
  tag: {width: '100%'},
  container: {
    width: '100%',
    backgroundColor: colors.palette.white,
  },
  redAsterisk: {color: colors.palette.errorRedDark},
  labelWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing[1],
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.palette.black,
  },
  optionalLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.palette.secondaryBlack5,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: colors.palette.stroke,
    borderRadius: corner_radius['rounded-md'],
    padding: spacing[2],
    justifyContent: 'space-between',
  },
  placeholder: {
    color: colors.palette.nuetral4,
    flex: 1,
    fontWeight: '500',
  },
  helperLabel: {
    fontSize: 12,
    color: colors.palette.secondaryBlack5,
    fontWeight: '400',
    marginTop: spacing[1],
  },
  errorLabel: {
    fontSize: 12,
    color: colors.palette.errorRedDark,
    fontWeight: '400',
    marginTop: spacing[1],
  },
  dropdownWrapper: {
    position: 'absolute',
    zIndex: 999,
    top: 30,
    right: 0,
    left: 0,
  },
  dropdownContainer: {
    position: 'absolute',
    zIndex: 999,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: colors.palette.white,
    borderRadius: corner_radius['rounded-md'],
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    maxWidth: '50%',
    marginLeft: 'auto',
  },
  optionItem: {
    padding: spacing[2],
    borderBottomWidth: 1,
    borderBottomColor: colors.palette.stroke,
  },
});

const sizeStyles: Record<sizeTypes, Partial<styleProps>> = {
  lg: {
    inputWrapper: {height: 48, fontSize: 18},
    label: {fontSize: 18},
  },
  md: {
    inputWrapper: {height: 40, fontSize: 16},
    label: {fontSize: 16},
  },
  sm: {
    inputWrapper: {height: 32, fontSize: 14},
    label: {fontSize: 14},
    placeholder: {fontSize: 14},
  },
};
